import { useTranslation } from 'react-i18next';
import { Field, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import { ReduxState } from 'ducks';
import { required } from 'lib/forms/validate';
import { LanguageLinkBar } from 'components/LanguageLinkBar/LanguageLinkBar';

import { InquiryBookingSummary } from './InquiryBookingSummary';
import styles from './InquiryForm.module.css';
import { InquiryFormValues } from './InquiryFormValues';

export const InquiryInputForm = () => {
  const { t, i18n } = useTranslation();

  const reservation = useSelector((state: ReduxState) => state.server.reservation.reservation);

  const allInquiryCategories = useSelector(
    (state: ReduxState) => state.server.settings.all?.inquiry_settings?.inquiry_categories ?? []
  );
  const inquiryCategories = allInquiryCategories.filter(
    (category) =>
      category.language === i18n.language.toUpperCase().replace('-', '_') &&
      (reservation || !category.reservation_inquiries_only)
  );
  const form = useForm<InquiryFormValues>();

  return (
    <div className={styles['post-main__frame']}>
      <p className={styles['post-main__ttl']}>{t('Submit an Inquiry')}</p>
      {reservation && <InquiryBookingSummary />}
      <p className={styles['post-main__subttl']}>{t('Inquiry Information')}</p>

      <div className={styles['post-form']}>
        {inquiryCategories.length > 0 && (
          <div className={styles['post-form__box']}>
            <div className={styles['post-form__box__ttl']}>
              {t('Category')}
              <span className={styles['required']}>*</span>
            </div>
            <Field<string> name="category" validate={required}>
              {(props) => (
                <div className={styles['post-form__box__body']}>
                  <select
                    value={props.input.value}
                    onChange={(e) => {
                      const newCategory = inquiryCategories.find(
                        (category) => category.title === e.target.value
                      );
                      if ((newCategory?.questions ?? []).length > 0) {
                        form.change(
                          'body',
                          newCategory?.questions
                            .map(
                              (question) =>
                                `${question.text}\n\n## ${t('Please Type Your Answer Here')} ##`
                            )
                            .join('\n\n')
                        );
                      }
                      props.input.onChange(e.target.value);
                    }}
                    className={styles['post-form__box__body__inputText']}
                  >
                    <option key="" value=""></option>
                    {inquiryCategories.map((category) => (
                      <option key={category.title} value={category.title}>
                        {category.title}
                      </option>
                    ))}
                  </select>
                  {props.meta.touched && props.meta.error && (
                    <p className={styles['post-form__err']}>{t(props.meta.error)}</p>
                  )}
                </div>
              )}
            </Field>
          </div>
        )}
        <div className={styles['post-form__box']}>
          <div className={styles['post-form__box__ttl']}>
            {t('Subject')}
            <span className={styles['required']}>*</span>
          </div>
          <Field<string> name="title" validate={required}>
            {(props) => (
              <div className={styles['post-form__box__body']}>
                <input
                  {...props.input}
                  type="text"
                  className={styles['post-form__box__body__inputText']}
                />
                {props.meta.touched && props.meta.error && (
                  <p className={styles['post-form__err']}>{t(props.meta.error)}</p>
                )}
              </div>
            )}
          </Field>
        </div>

        <div className={styles['post-form__box']}>
          <div className={styles['post-form__box__ttl']}>
            {t('Description')}
            <span className={styles['required']}>*</span>
          </div>
          <Field<string> name="body" validate={required}>
            {(props) => (
              <div className={styles['post-form__box__body']}>
                <textarea {...props.input} className={styles['post-form__box__body__textarea']} />
                {props.meta.touched && props.meta.error && (
                  <p className={styles['post-form__err']}>{t(props.meta.error)}</p>
                )}
              </div>
            )}
          </Field>
        </div>

        {!reservation && (
          <>
            {i18n.language === 'ja-JP' ? (
              <>
                <div className={styles['post-form__box']}>
                  <div className={styles['post-form__box__ttl']}>
                    {t('Your Family Name')}
                    <span className={styles['required']}>*</span>
                  </div>
                  <Field<string> name="familyName" validate={required}>
                    {({ input, meta: { touched, error } }) => (
                      <div className={styles['post-form__box__body']}>
                        <input
                          type="text"
                          className={styles['post-form__box__body__inputText']}
                          {...input}
                        />
                        {touched && error && <p className={styles['post-form__err']}>{t(error)}</p>}
                      </div>
                    )}
                  </Field>
                </div>
                <div className={styles['post-form__box']}>
                  <div className={styles['post-form__box__ttl']}>
                    {t('Your Given Name')}
                    <span className={styles['required']}>*</span>
                  </div>
                  <Field<string> name="givenName" validate={required}>
                    {({ input, meta: { touched, error } }) => (
                      <div className={styles['post-form__box__body']}>
                        <input
                          type="text"
                          className={styles['post-form__box__body__inputText']}
                          {...input}
                        />
                        {touched && error && <p className={styles['post-form__err']}>{t(error)}</p>}
                      </div>
                    )}
                  </Field>
                </div>
              </>
            ) : (
              <>
                <div className={styles['post-form__box']}>
                  <div className={styles['post-form__box__ttl']}>
                    {t('Your Given Name')}
                    <span className={styles['required']}>*</span>
                  </div>
                  <Field<string> name="givenName" validate={required}>
                    {({ input, meta: { touched, error } }) => (
                      <div className={styles['post-form__box__body']}>
                        <input
                          type="text"
                          className={styles['post-form__box__body__inputText']}
                          {...input}
                        />
                        {touched && error && <p className={styles['post-form__err']}>{t(error)}</p>}
                      </div>
                    )}
                  </Field>
                </div>
                <div className={styles['post-form__box']}>
                  <div className={styles['post-form__box__ttl']}>
                    {t('Your Family Name')}
                    <span className={styles['required']}>*</span>
                  </div>
                  <Field<string> name="familyName" validate={required}>
                    {({ input, meta: { touched, error } }) => (
                      <div className={styles['post-form__box__body']}>
                        <input
                          type="text"
                          className={styles['post-form__box__body__inputText']}
                          {...input}
                        />
                        {touched && error && <p className={styles['post-form__err']}>{t(error)}</p>}
                      </div>
                    )}
                  </Field>
                </div>
              </>
            )}
            <div className={styles['post-form__box']}>
              <div className={styles['post-form__box__ttl']}>
                {t('Your Email')}
                <span className={styles['required']}>*</span>
              </div>
              <Field<string> name="email" validate={required}>
                {({ input, meta: { touched, error } }) => (
                  <div className={styles['post-form__box__body']}>
                    <input
                      type="text"
                      className={styles['post-form__box__body__inputText']}
                      {...input}
                    />
                    {touched && error && <p className={styles['post-form__err']}>{t(error)}</p>}
                  </div>
                )}
              </Field>
            </div>
          </>
        )}

        <div className={styles['post-main__btns']}>
          <button className={styles['bg-color-main']} type="submit">
            {t('Confirm Info')}
          </button>
        </div>

        <LanguageLinkBar />
      </div>
    </div>
  );
};
